import { faWeightScale } from "@fortawesome/free-solid-svg-icons";
import { Chart, ChartData, CustomTooltipProps, formatShortChartDate, TitleStyle, TooltipContainer, TooltipText } from "./Chart";
import { useMemo } from "react";
import { DateTime } from "luxon";
import { Guard } from "../utils";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";

const CustomTooltip = ({ active, payload }: CustomTooltipProps): JSX.Element => {
  if (active && payload && payload.length) {
    const fat = payload[0].value as number;
    return (
      <TooltipContainer>
        <TooltipText>{i18n.t("splash.charts.bodyFat.tooltip", { percent: fat.toPrecision(2) })}</TooltipText>
      </TooltipContainer>
    );
  } else {
    return <></>;
  }
};

type BodyFatChartData = ChartData & {
  bodyFatPercentage: number;
};

const isBodyFatData: Guard<BodyFatChartData> = (data: unknown): data is BodyFatChartData => {
  return (data as BodyFatChartData).bodyFatPercentage !== undefined;
};

export function getBodyFatPredictionData({ data, showTitle }: { data: BodyFatChartData[]; showTitle: TitleStyle | undefined }) {
  if (data != null && data.length > 0) {
    const showYear = data[data.length - 1].date.year === DateTime.local().year;
    const predictedDate = formatShortChartDate(_.last(data)!.date, !showYear);
    const titlePrefix = TitleStyle.Text === showTitle ? i18n.t("splash.charts.titlePrefix") : i18n.t("splash.charts.bodyFat.iconTitle");
    const titlePrediction = i18n.t("splash.charts.bodyFat.prediction", { percent: _.last(data)!.bodyFatPercentage });
    const titlePredictionDate = predictedDate;

    return {
      titlePrefix,
      titlePrediction,
      titlePredictionDate,
    }
  }
  return null;
}

type BodyFatChartProps = {
  data: BodyFatChartData[];
  showTitle?: TitleStyle;
  showOnlyTitle?: boolean;
  showBigHeader?: boolean;
  dedupeMonthTicks?: boolean;
  bendMode?: boolean;
}

const BodyFatChart = (props: BodyFatChartProps) => {
  const { data, showTitle, showOnlyTitle, showBigHeader, dedupeMonthTicks, bendMode } = props;
  const { t, i18n: { language } } = useTranslation();
  const predictionData = useMemo(() => {
    return getBodyFatPredictionData({ data, showTitle });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, showTitle, language]);

  if (predictionData) {
    const { titlePrefix, titlePrediction, titlePredictionDate } = predictionData;

    return <Chart
      data={data}
      dataKey="bodyFatPercentage"
      showTitle={showTitle}
      icon={faWeightScale}
      CustomTooltip={CustomTooltip}
      titlePrefix={titlePrefix}
      titlePrediction={titlePrediction}
      titlePredictionDate={titlePredictionDate}
      finalMarker={`${titlePrediction} ${titlePredictionDate}`}
      caret={'▼'}
      markerLocation="bottom"
      yAxisLabel={t("splash.charts.bodyFat.yAxis")}
      showOnlyTitle={showOnlyTitle}
      showBigHeader={showBigHeader}
      dedupeMonthTicks={dedupeMonthTicks}
      bendMode={bendMode}
    />;
  } else {
    return <></>;
  }
};

export {
  BodyFatChart,
  isBodyFatData,
};
export type { BodyFatChartData };
